import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getProjectComments,
  createProjectComment,
  updateProjectComment,
  deleteProjectComment,
} from "../../services/api/api";

/**
 * Hook para carregar comentários de um projeto com paginação
 * Seguindo padrão de hooks de API do projeto JBE Engenharia
 */
export const useProjectComments = (projectId, page = 1, limit = 20) => {
  const { 
    isLoading: commentsLoading, 
    data: commentsData, 
    error: commentsError,
    refetch: refetchComments
  } = useQuery(
    ["projectComments", projectId, page, limit],
    () => getProjectComments(projectId, page, limit),
    {
      enabled: !!projectId, // Só executa se projectId estiver definido
      staleTime: 30000, // 30 segundos de cache
      retry: 1,
      onError: (error) => {
        console.error("Erro ao carregar comentários:", error);
        toast.error("Erro ao carregar comentários. Tente novamente.");
      },
    }
  );

  return {
    commentsLoading,
    commentsData: commentsData || { comments: [], pagination: { total: 0, totalPages: 0, page: 1, limit } },
    commentsError,
    refetchComments,
  };
};

/**
 * Hook para criar comentário em projeto
 */
export const useCreateProjectComment = () => {
  const queryClient = useQueryClient();

  const createCommentMutation = useMutation(createProjectComment, {
    onSuccess: (newComment, variables) => {
      // Atualiza cache local - adiciona comentário no início da lista
      queryClient.setQueryData(
        ["projectComments", variables.projectId, 1, 20],
        (oldData) => {
          if (!oldData) return oldData;
          
          return {
            ...oldData,
            comments: [newComment, ...oldData.comments],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        }
      );
      
      // Invalida outras páginas para garantir sincronização
      queryClient.invalidateQueries(["projectComments", variables.projectId]);
      // Invalida feed de atividades para exibir novo comentário
      queryClient.invalidateQueries(["projectActivityFeed", variables.projectId]);
      
      toast.success("Comentário adicionado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao criar comentário:", error);
      const errorMessage = error.response?.data?.message || "Erro ao adicionar comentário. Tente novamente.";
      toast.error(errorMessage);
    },
  });

  return {
    createComment: createCommentMutation.mutate,
    createCommentLoading: createCommentMutation.isLoading,
    createCommentError: createCommentMutation.error,
  };
};

/**
 * Hook para editar comentário
 */
export const useUpdateProjectComment = () => {
  const queryClient = useQueryClient();

  const updateCommentMutation = useMutation(
    ({ commentId, data }) => updateProjectComment(commentId, data),
    {
      onSuccess: (updatedComment) => {
        // Atualiza cache local - substitui comentário editado
        queryClient.setQueryData(
          ["projectComments"],
          (oldData) => {
            if (!oldData) return oldData;
            
            return {
              ...oldData,
              comments: oldData.comments.map((comment) =>
                comment.id === updatedComment.id ? updatedComment : comment
              ),
            };
          }
        );
        
        // Invalida queries para garantir sincronização
        queryClient.invalidateQueries(["projectComments"]);
        queryClient.invalidateQueries(["projectActivityFeed"]);
        
        toast.success("Comentário editado com sucesso!");
      },
      onError: (error) => {
        console.error("Erro ao editar comentário:", error);
        const errorMessage = error.response?.data?.message || "Erro ao editar comentário. Tente novamente.";
        toast.error(errorMessage);
      },
    }
  );

  return {
    updateComment: updateCommentMutation.mutate,
    updateCommentLoading: updateCommentMutation.isLoading,
    updateCommentError: updateCommentMutation.error,
  };
};

/**
 * Hook para excluir comentário
 */
export const useDeleteProjectComment = () => {
  const queryClient = useQueryClient();

  const deleteCommentMutation = useMutation(deleteProjectComment, {
    onSuccess: (_, commentId) => {
      // Atualiza cache local - remove comentário da lista
      queryClient.setQueryData(
        ["projectComments"],
        (oldData) => {
          if (!oldData) return oldData;
          
          return {
            ...oldData,
            comments: oldData.comments.filter((comment) => comment.id !== commentId),
            pagination: {
              ...oldData.pagination,
              total: Math.max(0, oldData.pagination.total - 1),
            },
          };
        }
      );
      
      // Invalida queries para garantir sincronização
      queryClient.invalidateQueries(["projectComments"]);
      queryClient.invalidateQueries(["projectActivityFeed"]);
      
      toast.success("Comentário excluído com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao excluir comentário:", error);
      const errorMessage = error.response?.data?.message || "Erro ao excluir comentário. Tente novamente.";
      toast.error(errorMessage);
    },
  });

  return {
    deleteComment: deleteCommentMutation.mutate,
    deleteCommentLoading: deleteCommentMutation.isLoading,
    deleteCommentError: deleteCommentMutation.error,
  };
};